import type { JobReportCrewMember } from "@/domain/model/JobReport";

// MUI Imports
import {
  List,
  ListItem,
  Avatar,
  Typography,
  Box,
  LinearProgress,
} from "@mui/material";

interface JobCrewListProps {
  crew: JobReportCrewMember[];
}

export function JobCrewList({ crew }: JobCrewListProps) {
  const formatTime = (seconds: number) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hrs}h ${mins.toString().padStart(2, "0")}m`;
  };

  const getInitials = (name: string) => {
    const parts = name.trim().split(" ");
    return parts.length >= 2
      ? `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
      : name.substring(0, 2).toUpperCase();
  };

  if (!crew || crew.length === 0) return null;

  const sortedCrew = [...crew].sort((a, b) => b.workSeconds - a.workSeconds);

  // Totals Calculation
  const totalWork = crew.reduce((acc, m) => acc + m.workSeconds, 0);
  const totalPresence = crew.reduce((acc, m) => acc + m.presenceSeconds, 0);

  return (
    <Box>
      <List disablePadding>
        {sortedCrew.map((member, index) => {
          const workRatio =
            member.presenceSeconds > 0
              ? (member.workSeconds / member.presenceSeconds) * 100
              : 0;

          return (
            <ListItem
              key={member.personId}
              divider={index !== sortedCrew.length - 1}
              sx={{
                px: 0,
                py: 1.5,
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
              }}
            >
              {/* Column 1: Avatar */}
              <Avatar
                sx={{
                  bgcolor: "primary.light",
                  color: "primary.main",
                  width: 36,
                  height: 36,
                  fontSize: "0.85rem",
                  fontWeight: "bold",
                  flexShrink: 0,
                }}
              >
                {getInitials(member.name)}
              </Avatar>

              {/* Column 2: Information Content */}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                {/* Row 1: Name */}
                <Typography
                  variant="body2"
                  fontWeight="700"
                  noWrap
                  sx={{ mb: 0.25 }}
                >
                  {member.name}
                </Typography>

                {/* Row 2: Time Details (Split Left/Right) */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    Anw: {formatTime(member.presenceSeconds)}
                  </Typography>

                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {formatTime(member.workSeconds)}
                  </Typography>
                </Box>

                {/* Row 3: Progress Bar */}
                <LinearProgress
                  variant="determinate"
                  value={workRatio}
                  sx={{
                    height: 4,
                    borderRadius: 2,
                    bgcolor: "grey.100",
                    "& .MuiLinearProgress-bar": {
                      bgcolor:
                        workRatio >= 90 ? "success.main" : "warning.main",
                    },
                  }}
                />
              </Box>
            </ListItem>
          );
        })}
      </List>

      {/* 5. Summary Section */}
      <Box
        sx={{
          mt: 2,
          p: 2,
          bgcolor: "grey.50",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "grey.200",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
          <Typography variant="caption" fontWeight="bold">
            Gesamt (Brutto):
          </Typography>
          <Typography variant="caption">{formatTime(totalPresence)}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Typography variant="body2" fontWeight="bold">
            Gesamt (Netto):
          </Typography>
          <Typography variant="body2" fontWeight="bold" color="primary.main">
            {formatTime(totalWork)}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
